import { AddressType, getAddressInfo } from 'bitcoin-address-validation';

import { WalletType } from '@/context/types';
import { getWalletConnector, loadWalletConnector } from '@/lib/util';

import { buildQuantaHeaders, readLiveWalletRoles, signQuantaAuth } from './auth';
import { type MigrationAuthority, type MigrationWallet, sameMigrationWallet } from './walletMigration';

/** Read the legacy extension directly: the app's MetaMask session stays on Signet. */
export async function readMigrationOwner(provider: WalletType, expected: MigrationAuthority): Promise<MigrationWallet> {
  if (provider !== WalletType.Xverse && provider !== WalletType.Unisat) throw new Error('MIGRATION_OWNER_WALLET_REQUIRED');
  const roles = await readLiveWalletRoles(provider);
  for (const role of Object.values(roles ?? {})) {
    if (!role) continue;
    try {
      const info = getAddressInfo(role.address);
      const identity: MigrationWallet = {
        address: /^(bc1|tb1)/i.test(role.address) ? role.address.toLowerCase() : role.address,
        publicKey: (info.type === AddressType.p2tr && role.publicKey.length === 66
          ? role.publicKey.slice(2)
          : role.publicKey
        ).toLowerCase(),
        network: info.network === 'mainnet' ? 'mainnet' : 'testnet',
      };
      const owner = expected.signingAuthority ?? expected.authority;
      const compatiblePayment =
        !expected.migrated &&
        identity.network === owner.network &&
        /^0[23][a-f0-9]{64}$/i.test(owner.publicKey) &&
        identity.publicKey === owner.publicKey.toLowerCase() &&
        ((/^(bc1q|tb1q)/i.test(owner.address) && /^[23]/.test(identity.address)) ||
          (/^[23]/.test(owner.address) && /^(bc1q|tb1q)/i.test(identity.address)));
      if (sameMigrationWallet(identity, owner) || compatiblePayment) return identity;
    } catch {
      // Malformed or unsupported identities cannot authorize this account.
    }
  }
  throw new Error('MIGRATION_OWNER_WALLET_REQUIRED');
}

export async function verifyMigrationOwner(provider: WalletType, expected: MigrationAuthority, assertCurrent: () => void) {
  if (provider !== WalletType.Xverse && provider !== WalletType.Unisat) throw new Error('MIGRATION_OWNER_WALLET_REQUIRED');
  await loadWalletConnector(provider);
  assertCurrent();
  const connector = getWalletConnector(provider);
  // Permission first; deliberately do not use client.connect, which enforces the app network.
  await connector.getWalletAccounts({ allowNetworkSwitch: false });
  assertCurrent();
  // Testnet and Signet share address encodings. Preserve a matching wallet's
  // current chain; only request a switch when its owner identity is not present.
  let identity: MigrationWallet;
  try {
    identity = await readMigrationOwner(provider, expected);
  } catch {
    assertCurrent();
    if ((await connector.switchNetwork(expected.authority.network === 'mainnet' ? 'main' : 'signet')) === false)
      throw new Error('MIGRATION_OWNER_NETWORK_REQUIRED');
    assertCurrent();
    identity = await readMigrationOwner(provider, expected);
  }
  assertCurrent();
  const signed = await signQuantaAuth({ address: identity.address, pubkey: identity.publicKey, client: connector });
  assertCurrent();
  const after = await readMigrationOwner(provider, { ...expected, signingAuthority: identity });
  assertCurrent();
  if (!sameMigrationWallet(identity, after)) throw new Error('MIGRATION_SOURCE_CHANGED');
  return { identity, headers: buildQuantaHeaders(signed) };
}
